import React, { useCallback, useEffect, useState } from "react";
import { motion as Motion, AnimatePresence } from "framer-motion";
import AdminHeader from "../../component/AdminHeader";
import { ArrowDown, ArrowUp, Eye, EyeOff, X } from "lucide-react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { http } from "../../axios/axios";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import ActionButtons from "../../component/AdminActionButtons";
import Pagination from "../../component/Pagination";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

const InputField = ({
  label,
  name,
  type = "text",
  placeholder,
  register,
  errors = {},
  validation = {},
  as = "input",
  rows = 4,
  options = [],
}) => {
  const fieldError = name.split(".").reduce((value, key) => value?.[key], errors);
  const inputClass =
    "w-full border border-white/10 bg-white/5 text-white px-4 py-2 rounded-xl mt-2 outline-none focus:border-blue-500 transition";

  return (
    <div>
      <label
        htmlFor={name}
        className="mb-1.5 block text-sm font-semibold text-slate-300 md:mb-2 md:text-base"
      >
        {label}
        {validation?.required && <span className="text-red-500"> *</span>}
      </label>

      {as === "textarea" ? (
        <textarea
          id={name}
          rows={rows}
          placeholder={placeholder}
          {...register(name, validation)}
          aria-invalid={!!fieldError}
          className={`${inputClass} resize-none`}
        />
      ) : as === "select" ? (
        <select
          id={name}
          {...register(name, validation)}
          aria-invalid={!!fieldError}
          className={inputClass}
        >
          <option value="" className="bg-[#0a0f1a] text-white">{placeholder}</option>

          {options.map((item) => (
            <option key={item.value} value={item.value} className="bg-[#0a0f1a] text-white">
              {item.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={name}
          type={type}
          placeholder={placeholder}
          {...register(name, validation)}
          aria-invalid={!!fieldError}
          className={inputClass}
        />
      )}

      {fieldError && (
        <p className="mt-1 text-xs text-red-500 md:text-sm">
          {fieldError.message}
        </p>
      )}
    </div>
  );
};

const AdminBlogsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [allBlogs, setAllBlogs] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [featuredFilter, setFeaturedFilter] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
  });

  const handleDelete = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete this blog?",
      );

      if (!confirmDelete) return;

      const res = await http.delete(`/blogs/${id}`);
      if (res.data.success) {
        await fetchBlog();
        toast.success("Blog deleted successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete blog");
    }
  };

  const fetchBlog = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: String(pagination.limit),
      });
      if (search.trim()) params.set("search", search.trim());
      if (categoryFilter) params.set("category", categoryFilter);
      if (statusFilter) params.set("status", statusFilter);
      if (featuredFilter) params.set("featured", featuredFilter);
      const res = await http.get(`/blogs?${params.toString()}`);

      setAllBlogs(res.data.data || []);

      setPagination((prev) => ({
        ...prev,
        totalPages: res.data.totalPages,
      }));
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load blogs");
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, featuredFilter, pagination.limit, pagination.page, search, statusFilter]);

  const [selectedBlog, setSelectedBlog] = useState(null);

  const fetchBlogById = async (id) => {
    try {
      const res = await http.get(`blogs/${id}`);
      setSelectedBlog(res.data.data);
      return res.data.data;
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to load blog");
    }
  };
  const handleEdit = async (item) => {
    const blog = await fetchBlogById(item._id);

    if (blog) {
      setIsModalOpen(true);
    }
  };

  useEffect(() => {
    fetchBlog();
  }, [fetchBlog]);

  const togglePublishStatus = async (item) => {
    const nextStatus = item.status === "Publish" ? "Draft" : "Publish";
    try {
      await http.patch(`/blogs/${item._id}/status`, { status: nextStatus });
      await fetchBlog();
      toast.success(nextStatus === "Publish" ? "Blog published" : "Blog unpublished");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update blog status");
    }
  };

  return (
    <div className="p-4 md:p-8 min-h-screen text-slate-200">
      <AdminHeader
        title="Manage Blogs"
        buttonText="Add Blog"
        onClick={() => {
          setSelectedBlog(null);
          setIsModalOpen(true);
        }}
      />

      <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          placeholder="Search blog title"
          className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-white placeholder:text-slate-400"
        />
        <select
          value={categoryFilter}
          onChange={(event) => {
            setCategoryFilter(event.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-lg border border-white/10 bg-slate-900 px-4 py-2 text-white"
        >
          <option value="">All categories</option>
          {[...new Set(allBlogs.map((blog) => blog.blogCategory).filter(Boolean))].map((category) => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-lg border border-white/10 bg-slate-900 px-4 py-2 text-white"
        >
          <option value="">All statuses</option>
          <option value="Publish">Published</option>
          <option value="Draft">Draft</option>
        </select>
        <select
          value={featuredFilter}
          onChange={(event) => {
            setFeaturedFilter(event.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
          className="rounded-lg border border-white/10 bg-slate-900 px-4 py-2 text-white"
        >
          <option value="">All featured values</option>
          <option value="true">Featured</option>
          <option value="false">Not featured</option>
        </select>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-slate-400">Loading blogs...</div>
      ) : allBlogs.length === 0 ? (
        <div className="p-8 rounded-2xl bg-white/5 border border-white/10 text-center text-slate-400">
          No blogs found
        </div>
      ) : (
        <div className="space-y-4">
          {allBlogs.map((item) => (
            <div
              key={item._id}
              className="       group
              flex
              flex-col
              lg:flex-row
              lg:items-center
              justify-between
              gap-4
              p-4
              rounded-2xl
              bg-slate-900/40
              border border-slate-800
              hover:border-slate-700
              hover:bg-slate-900/60
              transition-all"
            >
              <div className="flex items-center gap-4 flex-1 min-w-0 ">
                <div
                  className="h-12 w-12 
                      sm:h-16 sm:w-16
                      rounded
                      bg-whitep-2
                      flex
                      items-center
                      justify-center
                      shrink-0 
                      "
                >
                  <img
                    src={GetImageUrl(item.blogBannerImage)}
                    className="max-h-full max-w-full object-contain "
                  />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm lg:text-base">
                    {item.blogHeading}
                  </h3>
                  <p className="text-sm text-slate-400 py-1">
                    {item?.status?.toUpperCase()}
                  </p>
                  <p className="text-xs text-slate-400">
                    {item.blogCategory} {item.author ? `| ${item.author}` : ""}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">
                    Order: {item.displayOrder ?? 0}
                    {item.isFeatured ? " | Featured" : ""}
                    {item.isTrending ? " | Trending" : ""}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Created {item.createdAt ? new Date(item.createdAt).toLocaleDateString() : "-"}
                    {item.updatedAt ? ` | Updated ${new Date(item.updatedAt).toLocaleDateString()}` : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 ">
                {item.status === "Publish" && item.slug && (
                  <Link
                    to={`/blogs/${item.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Preview published blog"
                    className="rounded-lg border border-white/10 p-2 text-slate-300 hover:bg-white/10"
                  >
                    <Eye size={18} />
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => togglePublishStatus(item)}
                  title={item.status === "Publish" ? "Unpublish blog" : "Publish blog"}
                  className="rounded-lg border border-white/10 p-2 text-slate-300 hover:bg-white/10"
                >
                  {item.status === "Publish" ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
                <ActionButtons
                  onEdit={() => handleEdit(item)}
                  onDelete={() => handleDelete(item._id)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      {allBlogs.length > 0 && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          onPrevious={() =>
            setPagination((prev) => ({
              ...prev,
              page: Math.max(prev.page - 1, 1),
            }))
          }
          onNext={() =>
            setPagination((prev) => ({
              ...prev,
              page: Math.min(prev.page + 1, prev.totalPages),
            }))
          }
        />
      )}

      <AnimatePresence>
        {isModalOpen && (
          <CreateBlog
            key={selectedBlog?._id || "new-blog"}
            setIsModalOpen={setIsModalOpen}
            blog={selectedBlog}
            fetchBlog={fetchBlog}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminBlogsPage;

const CreateBlog = ({ setIsModalOpen, blog, fetchBlog }) => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      blogCategory: blog?.blogCategory || "",
      blogHeading: blog?.blogHeading || "",
      author: blog?.author || "",
      blogBannerImageAlt: blog?.blogBannerImageAlt || "",
      blogBannerImageTitle: blog?.blogBannerImageTitle || "",
      blogShortDescription: blog?.blogShortDescription || "",
      status: blog?.status || "",
      displayOrder: blog?.displayOrder ?? 0,
      isFeatured: blog?.isFeatured || false,
      isTrending: blog?.isTrending || false,
      slug: blog?.slug || "",
      schemaScript: blog?.schemaScript || "",
      socialLinks: {
        facebook: blog?.socialLinks?.facebook || "",
        x: blog?.socialLinks?.x || "",
        linkedin: blog?.socialLinks?.linkedin || "",
        instagram: blog?.socialLinks?.instagram || "",
      },
      metaTitle: blog?.metaFields?.[0]?.title || "",
      metaDescription: blog?.metaFields?.[0]?.description || "",
      metaKeywords: blog?.metaFields?.[0]?.keywords?.join(", ") || "",
      metaCanonicalUrl: blog?.metaFields?.[0]?.canonicalUrl || "",
      metaOgTitle: blog?.metaFields?.[0]?.ogTitle || "",
      metaOgDescription: blog?.metaFields?.[0]?.ogDescription || "",
      metaOgImage: blog?.metaFields?.[0]?.ogImage || "",
      content: blog?.content?.length
        ? blog.content.map((item) => ({
            heading: item.heading,
            shortDescription: item.shortDescription,
            imageUrl: "",
            existingImage: item.imageUrl,
            imageAlt: item.imageAlt,
            youtubeUrl: item.youtubeUrl,
            caption: item.caption,
            description: item.description,
          }))
        : [{ heading: "", shortDescription: "", imageUrl: "", imageAlt: "", youtubeUrl: "", caption: "", description: "" }],
    },
  });
  const [blogBannerImage, setBlogBannerImage] = useState({
    existing: blog?.blogBannerImage
      ? [{ url: blog.blogBannerImage, alt: blog.blogBannerImageAlt }]
      : [],
    new: [],
    removed: [],
  });

  const [contentImages, setContentImages] = useState(
    blog?.content?.map((item) => ({
      existing: item.imageUrl ? [{ url: item.imageUrl, alt: item.imageAlt }] : [],
      new: [],
      removed: [],
    })) || [],
  );
  const [contentType, setContentType] = useState(
    blog?.content?.length
      ? blog.content.map((item) => (item.youtubeUrl ? "video" : "image"))
      : ["image"],
  );
  const [videoData, setVideoData] = useState(
    blog?.content?.length
      ? blog.content.map((item) => ({
          youtubeUrl: item.youtubeUrl || "",
          caption: item.caption || "",
        }))
      : [
          {
            youtubeUrl: "",
            caption: "",
          },
        ],
  );

  const isEdit = !!blog;
  const titleValue = useWatch({ control, name: "blogHeading" });
  const slugValue = useWatch({ control, name: "slug" });

  useEffect(() => {
    if (!slugValue && titleValue) {
      setValue(
        "slug",
        titleValue
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, ""),
      );
    }
  }, [setValue, slugValue, titleValue]);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "content",
  });

  const removeBannerImage = () => {
    setBlogBannerImage((prev) => {
      if (prev.new.length) {
        prev.new.forEach((img) => URL.revokeObjectURL(img.prev));

        return {
          ...prev,
          new: [],
        };
      }

      if (prev.existing.length) {
        return {
          existing: [],
          new: [],
          removed: [...prev.removed, prev.existing[0]],
        };
      }

      return prev;
    });
  };

  const removeContentImage = (index) => {
    setContentImages((prev) => {
      const updated = [...prev];

      if (!updated[index]) return updated;

      if (updated[index].new.length) {
        updated[index].new.forEach((img) => URL.revokeObjectURL(img.preview));

        updated[index].new = [];
      } else if (updated[index].existing.length) {
        updated[index].removed = [
          ...updated[index].removed,
          ...updated[index].existing,
        ];

        updated[index].existing = [];
      }

      return updated;
    });
  };

  const switchToImage = (index) => {
    setVideoData((prev) => {
      const arr = [...prev];
      arr[index] = {
        youtubeUrl: "",
        caption: "",
      };
      return arr;
    });

    setValue(`content.${index}.youtubeUrl`, "");
    setValue(`content.${index}.caption`, "");

    setContentType((prev) => {
      const arr = [...prev];
      arr[index] = "image";
      return arr;
    });
  };

  const switchToVideo = (index) => {
    removeContentImage(index);

    setValue(`content.${index}.imageUrl`, "");
    setValue(`content.${index}.imageAlt`, "");

    setVideoData((prev) => {
      const arr = [...prev];

      if (!arr[index]) {
        arr[index] = {
          youtubeUrl: "",
          caption: "",
        };
      }

      return arr;
    });

    setContentType((prev) => {
      const arr = [...prev];
      arr[index] = "video";
      return arr;
    });
  };

  const submitForm = async (data) => {
    if (!blogBannerImage.new.length && !blogBannerImage.existing.length) {
      setError("blogBannerImage", {
        type: "required",
        message: "Featured image is required.",
      });
      document.querySelector('[name="blogBannerImage"]')?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    try {
      const formData = new FormData();
      if (blogBannerImage.new.length) {
        formData.append("blogBannerImage", blogBannerImage.new[0].file);
      }

      if (blogBannerImage.removed.length && !blogBannerImage.new.length) {
        formData.append("removeBannerImage", "true");
      }

      formData.append("blogCategory", data.blogCategory);
      formData.append("blogHeading", data.blogHeading);
      formData.append("author", data.author);
      formData.append("blogBannerImageAlt", data.blogBannerImageAlt);
      formData.append("blogBannerImageTitle", data.blogBannerImageTitle);
      formData.append("blogShortDescription", data.blogShortDescription);
      formData.append("status", data.status);
      formData.append("displayOrder", String(data.displayOrder || 0));
      formData.append("isFeatured", String(!!data.isFeatured));
      formData.append("isTrending", String(!!data.isTrending));
      formData.append("slug", data.slug);
      formData.append("schemaScript", data.schemaScript);
      formData.append("socialLinks", JSON.stringify(data.socialLinks));

      formData.append(
        "metaFields",
        JSON.stringify([
          {
            title: data.metaTitle,
            description: data.metaDescription,
            canonicalUrl: data.metaCanonicalUrl,
            ogTitle: data.metaOgTitle,
            ogDescription: data.metaOgDescription,
            ogImage: data.metaOgImage,
            keywords:
              data.metaKeywords
                ?.split(",")
                .map((k) => k.trim())
                .filter(Boolean) || [],
          },
        ]),
      );

      const content = data.content.map((item, index) => {
        const section = {
          heading: item.heading,
          shortDescription: item.shortDescription,
          description: item.description,
          hasNewImage: !!contentImages[index]?.new?.length,
        };

        if (contentType[index] === "image") {
          section.hasImage = true;
          section.imageAlt = item.imageAlt;
          section.existingImage =
            contentImages[index]?.existing?.[0]?.url || "";
          section.removedImage = contentImages[index]?.removed?.[0]?.url || "";

          if (contentImages[index]?.new?.length) {
            formData.append("contentImages", contentImages[index].new[0].file);
          }
        } else {
          section.hasImage = false;
          section.youtubeUrl = videoData[index]?.youtubeUrl || "";
          section.caption = videoData[index]?.caption || "";
          section.existingImage = "";
          section.removedImage = contentImages[index]?.removed?.[0]?.url || "";
        }

        return section;
      });

      formData.append("content", JSON.stringify(content));

      let response;

      if (isEdit) {
        response = await http.put(`/blogs/${blog._id}`, formData);
      } else {
        response = await http.post("/blogs", formData);
      }
      if (response.data.success) {
        await fetchBlog();
        setIsModalOpen(false);
        toast.success(isEdit ? "Blog updated successfully" : "Blog created successfully");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save blog");
    }
  };
  return (
    <Motion.div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50">
      <Motion.div
        className="
        fixed z-50
        top-1/2 left-1/2
        -translate-x-1/2 -translate-y-1/2
        w-[95%] 
        h-[90vh]
        bg-[#0a0f1a]
        border border-white/10
        rounded-2xl
        shadow-2xl
        overflow-hidden
        flex flex-col
        "
      >
        <div
          className="
          flex items-center justify-between
          px-8 py-5
          border-b border-white/10
          bg-white/3
          "
        >
          <div>
            <h3 className="text-xl font-black text-white uppercase">
              {isEdit ? "Update Blog" : "Create Blog"}
            </h3>
          </div>

          <button
            type="button"
            onClick={() => {
              setIsModalOpen(false);
            }}
            className="
            h-10 w-10
            rounded-xl
            bg-white/5
            hover:bg-red-500
            text-slate-300
            flex items-center
            justify-center
            "
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(submitForm, () => {
            document.querySelector('[aria-invalid="true"]')?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          })}
          className="p-4 md:p-8 space-y-6 overflow-y-auto flex-1"
        >
          <InputField
            label="Blog Category"
            name="blogCategory"
            placeholder="Enter blog category (e.g. Technology, Business, Health)"
            register={register}
            errors={errors}
            validation={{
              required: "Blog category is required",
            }}
          />
          <InputField
            label="Blog Heading"
            name="blogHeading"
            placeholder='e.g. "Top 10 React Best Practices in 2026"'
            register={register}
            errors={errors}
            validation={{
              required: "Blog heading is required",
            }}
          />
          <InputField
            label="Author"
            name="author"
            placeholder="Enter the author's name"
            register={register}
            errors={errors}
            validation={{
              required: "Author is required",
            }}
          />{" "}
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-300">
              Banner Image
            </label>

            <input
              type="file"
              name="blogBannerImage"
              aria-invalid={!!errors.blogBannerImage}
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                clearErrors("blogBannerImage");

                setBlogBannerImage((prev) => {
                  prev.new.forEach((img) => URL.revokeObjectURL(img.prev));
                  return {
                    existing: [],
                    removed: prev.existing.length
                      ? [...prev.removed, ...prev.existing]
                      : prev.removed,
                    new: [
                      {
                        file,
                        prev: URL.createObjectURL(file),
                      },
                    ],
                  };
                });
              }}
              className="w-full rounded-lg border border-gray-600 bg-transparent p-2 text-white"
            />

            {errors.blogBannerImage && (
              <p className="mt-1 text-sm text-red-500">
                {errors.blogBannerImage.message}
              </p>
            )}

            {blogBannerImage.new.length > 0 ? (
              <div className="relative mt-4">
                <button
                  type="button"
                  onClick={removeBannerImage}
                  className="
        absolute
        top-2
        right-2
        z-10
        h-9
        w-9
        rounded-full
        bg-red-600
        hover:bg-red-700
        flex
        items-center
        justify-center
        text-white
        shadow-lg
      "
                >
                  <X size={18} />
                </button>

                <img
                  src={blogBannerImage.new[0].prev}
                  alt="Banner Preview"
                  className="h-56 w-full rounded-lg border border-white/10 bg-slate-900 object-contain"
                />
              </div>
            ) : blogBannerImage.existing.length > 0 ? (
              <div className="relative mt-4">
                <button
                  type="button"
                  onClick={removeBannerImage}
                  className="
        absolute
        top-2
        right-2
        z-10
        h-9
        w-9
        rounded-full
        bg-red-600
        hover:bg-red-700
        flex
        items-center
        justify-center
        text-white
        shadow-lg
      "
                >
                  <X size={18} />
                </button>

                <img
                  src={GetImageUrl(blogBannerImage.existing[0].url)}
                  alt="Existing Banner"
                  className="h-56 w-full rounded-lg border border-white/10 bg-slate-900 object-contain"
                />
              </div>
            ) : null}
          </div>
          <InputField
            label="Blog Banner Image Alt"
            name="blogBannerImageAlt"
            placeholder="Enter the banner image alt text"
            register={register}
            errors={errors}
            validation={{
              required: "Banner image alt text is required",
            }}
          />
          <InputField
            label="Banner Title"
            name="blogBannerImageTitle"
            placeholder="Enter the banner title"
            register={register}
            errors={errors}
            validation={{
              required: "Banner title is required",
            }}
          />
          <InputField
            label="Blog Short Description"
            name="blogShortDescription"
            as="textarea"
            rows={3}
            placeholder="Enter a short description of the blog"
            register={register}
            errors={errors}
            validation={{
              required: "Blog short description is required",
            }}
          />
          <InputField
            label="Status"
            name="status"
            as="select"
            placeholder="Select Status"
            options={[
              { value: "Draft", label: "Draft" },
              { value: "Publish", label: "Publish" },
            ]}
            register={register}
            errors={errors}
            validation={{
              required: "Status is required",
            }}
          />
          <InputField
            label="Display Order"
            name="displayOrder"
            type="number"
            register={register}
            validation={{ valueAsNumber: true }}
          />
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" {...register("isFeatured")} />
            Featured blog
          </label>
          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" {...register("isTrending")} />
            Trending blog
          </label>
          <InputField
            label="Facebook"
            name="socialLinks.facebook"
            placeholder="Enter the Facebook profile URL"
            register={register}
          />
          <InputField
            label="X (Twitter)"
            name="socialLinks.x"
            placeholder="Enter the X (Twitter) profile URL"
            register={register}
          />
          <InputField
            label="LinkedIn"
            name="socialLinks.linkedin"
            placeholder="Enter the LinkedIn profile URL"
            register={register}
          />
          <InputField
            label="Instagram"
            name="socialLinks.instagram"
            placeholder="Enter the Instagram profile URL"
            register={register}
          />
          {fields.map((field, index) => {
            const hasExistingImage =
              isEdit && contentImages[index]?.existing?.length > 0;

            const hasExistingVideo = isEdit && !!videoData[index]?.youtubeUrl;

            return (
              <div
                key={field.id}
                className="space-y-4 rounded-lg border border-white/10 bg-white/5 p-5"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-lg font-semibold text-white">
                    Content Section {index + 1}
                  </h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      title="Move section up"
                      aria-label="Move section up"
                      disabled={index === 0}
                      onClick={() => move(index, index - 1)}
                      className="rounded border border-white/10 p-2 text-white disabled:opacity-40"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      type="button"
                      title="Move section down"
                      aria-label="Move section down"
                      disabled={index === fields.length - 1}
                      onClick={() => move(index, index + 1)}
                      className="rounded border border-white/10 p-2 text-white disabled:opacity-40"
                    >
                      <ArrowDown size={16} />
                    </button>
                  </div>
                </div>

                <InputField
                  label="Content Heading"
                  name={`content.${index}.heading`}
                  placeholder="Enter the content heading"
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Content heading is required",
                  }}
                />

                <InputField
                  label="Short Description"
                  name={`content.${index}.shortDescription`}
                  placeholder="Enter the short description"
                  as="textarea"
                  rows={2}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Content short description is required",
                  }}
                />
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={hasExistingVideo}
                    onClick={() => switchToImage(index)}
                    className={`rounded-lg px-5 py-2 ${
                      contentType[index] === "image"
                        ? "bg-blue-600 text-white"
                        : "bg-slate-700 text-gray-300"
                    }`}
                  >
                    Image
                  </button>

                  <button
                    type="button"
                    disabled={hasExistingImage}
                    onClick={() => switchToVideo(index)}
                    className={`rounded-lg px-5 py-2 ${
                      contentType[index] === "video"
                        ? "bg-blue-600 text-white"
                        : "bg-slate-700 text-gray-300"
                    }`}
                  >
                    Video
                  </button>
                </div>
                {contentType[index] === "image" && (
                  <>
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-300">
                        Content Image
                      </label>

                      <input
                        type="file"
                        name={`contentImages.${index}`}
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;

                          setContentImages((prev) => {
                            const updated = [...prev];

                            if (!updated[index]) {
                              updated[index] = {
                                existing: [],
                                new: [],
                                removed: [],
                              };
                            }

                            updated[index].new.forEach((img) =>
                              URL.revokeObjectURL(img.preview),
                            );

                            updated[index] = {
                              existing: [],
                              removed: updated[index].existing.length
                                ? [
                                    ...updated[index].removed,
                                    ...updated[index].existing,
                                  ]
                                : updated[index].removed,
                              new: [
                                {
                                  file,
                                  preview: URL.createObjectURL(file),
                                },
                              ],
                            };

                            return updated;
                          });
                        }}
                        className="w-full rounded-lg border border-gray-600 bg-transparent p-2 text-white"
                      />

                      {contentImages[index]?.new.length > 0 ? (
                        <div className="relative mt-4">
                          <button
                            type="button"
                            onClick={() => removeContentImage(index)}
                            className="absolute top-2 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
                          >
                            <X size={18} />
                          </button>

                          <img
                            src={contentImages[index].new[0].preview}
                            className="h-56 w-full rounded-lg border border-white/10 bg-slate-900 object-contain"
                            alt=""
                          />
                        </div>
                      ) : contentImages[index]?.existing.length > 0 ? (
                        <div className="relative mt-4">
                          <button
                            type="button"
                            onClick={() => removeContentImage(index)}
                            className="absolute top-2 right-2 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700"
                          >
                            <X size={18} />
                          </button>

                          <img
                            src={GetImageUrl(
                              contentImages[index].existing[0].url,
                            )}
                            className="h-56 w-full rounded-lg border border-white/10 bg-slate-900 object-contain"
                            alt=""
                          />
                        </div>
                      ) : null}
                    </div>

                    <InputField
                      label="Image Alt"
                      name={`content.${index}.imageAlt`}
                      placeholder="Enter the image alt text"
                      register={register}
                    />
                  </>
                )}

                {contentType[index] === "video" && (
                  <>
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-300">
                        YouTube URL
                      </label>

                      <input
                        type="text"
                        {...register(`content.${index}.youtubeUrl`)}
                        value={videoData[index]?.youtubeUrl || ""}
                        className="w-full border border-white/10 bg-white/5 text-white px-4 py-2 rounded-xl mt-2 outline-none focus:border-blue-500 transition"
                        onChange={(e) => {
                          const updated = [...videoData];
                          updated[index].youtubeUrl = e.target.value;
                          setVideoData(updated);
                          setValue(
                            `content.${index}.youtubeUrl`,
                            e.target.value,
                          );
                        }}
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-semibold text-slate-300">
                        Caption
                      </label>

                      <input
                        type="text"
                        className="w-full border border-white/10 bg-white/5 text-white px-4 py-2 rounded-xl mt-2 outline-none focus:border-blue-500 transition"
                        {...register(`content.${index}.caption`)}
                        value={videoData[index]?.caption || ""}
                        onChange={(e) => {
                          const updated = [...videoData];
                          updated[index].caption = e.target.value;
                          setVideoData(updated);
                          setValue(`content.${index}.caption`, e.target.value);
                        }}
                      />
                    </div>
                  </>
                )}

                <InputField
                  label="Description"
                  name={`content.${index}.description`}
                  placeholder="Enter the detailed content description"
                  as="textarea"
                  rows={6}
                  register={register}
                  errors={errors}
                  validation={{
                    required: "Description is required",
                  }}
                />

                {fields.length > 1 && (
                  <button
                    type="button"
                    onClick={() => {
                      remove(index);

                      setContentImages((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                      setVideoData((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                      setContentType((prev) =>
                        prev.filter((_, i) => i !== index),
                      );
                    }}
                    className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700"
                  >
                    Remove Section
                  </button>
                )}
              </div>
            );
          })}
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                append({
                  heading: "",
                  shortDescription: "",
                  description: "",
                });

                setContentImages((prev) => [
                  ...prev,
                  {
                    existing: [],
                    new: [],
                    removed: [],
                  },
                ]);

                setVideoData((prev) => [
                  ...prev,
                  {
                    youtubeUrl: "",
                    caption: "",
                  },
                ]);
                setContentType((prev) => [...prev, "image"]);
              }}
              className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition hover:bg-blue-700"
            >
              + Add Content Section
            </button>
          </div>
          <InputField
            label="Slug"
            name="slug"
            placeholder="Enter the slug"
            register={register}
            errors={errors}
            validation={{ required: "Blog slug is required" }}
          />
          <InputField
            label="Meta Title"
            name="metaTitle"
            placeholder="Enter the meta title"
            register={register}
          />
          <InputField
            label="Meta Description"
            name="metaDescription"
            placeholder="Enter the meta description"
            as="textarea"
            rows={2}
            register={register}
          />
          <InputField
            label="Meta Keywords"
            name="metaKeywords"
            placeholder="Enter meta keywords separated by commas (e.g. React, JavaScript, Web Development)"
            register={register}
          />
          <InputField
            label="Canonical URL"
            name="metaCanonicalUrl"
            placeholder="https://example.com/blog/article"
            register={register}
          />
          <InputField
            label="Open Graph Title"
            name="metaOgTitle"
            placeholder="Social sharing title"
            register={register}
          />
          <InputField
            label="Open Graph Description"
            name="metaOgDescription"
            placeholder="Social sharing description"
            as="textarea"
            rows={2}
            register={register}
          />
          <InputField
            label="Open Graph Image URL"
            name="metaOgImage"
            placeholder="https://example.com/images/blog-share.jpg"
            register={register}
          />
          <InputField
            label="Schema Script"
            name="schemaScript"
            placeholder="Paste the JSON-LD schema script"
            as="textarea"
            rows={6}
            register={register}
          />
          <div className="mt-8 flex items-center justify-end gap-4 border-t border-white/10 pt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="rounded-lg border border-white/20 px-6 py-3 text-white transition hover:bg-white/10"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-lg bg-blue-600 px-6 py-3 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting
                ? isEdit ? "Updating..." : "Creating..."
                : isEdit ? "Update Blog" : "Create Blog"}
            </button>
          </div>
        </form>
      </Motion.div>
    </Motion.div>
  );
};